import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Box } from '@mui/material';
import DOMPurify from 'dompurify';

interface ArtifactRendererProps {
  content: string;
  contentType: 'react' | 'html' | 'javascript';
  toolResults?: Record<string, any[]>; // Optional data to inject as props
}

// Transform ES6 imports to global variables (moved outside component to avoid hook dependency)
const transformImports = (code: string) => {
  // Import mapping for global variables
  const importMap: Record<string, string> = {
    react: 'React',
    'react-dom': 'ReactDOM',
    recharts: 'RECHARTS_COMPONENTS', // Special handling for recharts
    'lucide-react': 'LUCIDE_COMPONENTS', // Special handling for lucide
    d3: 'window.d3',
    lodash: 'window._',
  };

  let transformed = code;

  // Special handling for Recharts - components should be in window.Recharts
  const rechartsImportRegex = /import\s+{([^}]+)}\s+from\s+['"]recharts['"]/g;
  transformed = transformed.replace(rechartsImportRegex, (match, imports) => {
    // Split imports and create individual assignments from window.Recharts
    const importList = imports.split(',').map((imp: any) => imp.trim());
    const assignments = importList
      .map((comp: any) => `const ${comp} = window.Recharts?.${comp};`)
      .join(' ');
    return assignments;
  });

  // Special handling for Lucide - components should be React wrappers
  const lucideImportRegex = /import\s+{([^}]+)}\s+from\s+['"]lucide-react['"]/g;
  transformed = transformed.replace(lucideImportRegex, (match, imports) => {
    // Split imports and create individual assignments from window.LucideReactComponents
    const importList = imports.split(',').map((imp: any) => imp.trim());
    const assignments = importList
      .map((comp: any) => `const ${comp} = window.LucideReactComponents?.${comp};`)
      .join(' ');
    return assignments;
  });

  // Handle named imports: import { LineChart, Bar } from 'other-libs'
  const namedImportRegex = /import\s+{([^}]+)}\s+from\s+['"]([^'"]+)['"]/g;
  transformed = transformed.replace(namedImportRegex, (match, imports, library) => {
    if (library === 'recharts' || library === 'lucide-react') return match; // Already handled above
    const globalVar = importMap[library];
    return globalVar ? `const {${imports}} = ${globalVar};` : match;
  });

  // Handle default imports: import React from 'react'
  const defaultImportRegex = /import\s+(\w+)\s+from\s+['"]([^'"]+)['"]/g;
  transformed = transformed.replace(defaultImportRegex, (match, name, library) => {
    const globalVar = importMap[library];
    return globalVar ? `const ${name} = ${globalVar};` : match;
  });

  // Handle mixed imports: import React, { useState } from 'react'
  const mixedImportRegex = /import\s+(\w+),\s*{([^}]+)}\s+from\s+['"]([^'"]+)['"]/g;
  transformed = transformed.replace(
    mixedImportRegex,
    (match, defaultName, namedImports, library) => {
      const globalVar = importMap[library];
      if (globalVar) {
        return `const ${defaultName} = ${globalVar}; const {${namedImports}} = ${globalVar};`;
      }
      return match;
    },
  );

  // Handle export statements
  // First check if there's already an App function defined
  const hasAppFunction = /function\s+App\s*\(/g.test(transformed);

  if (hasAppFunction) {
    // If App function exists, just remove export statements
    transformed = transformed.replace(/export\s+default\s+\w+;?\s*$/gm, '');
  } else {
    // If no App function, convert export default to App function
    // export default ComponentName; -> function App() { return <ComponentName />; }
    transformed = transformed.replace(
      /export\s+default\s+(\w+);?\s*$/gm,
      'function App() { return <$1 />; }',
    );
  }

  // export const/function -> const/function (remove export keyword)
  transformed = transformed.replace(/export\s+(const|function|let|var)\s+/g, '$1 ');

  return transformed;
};

export const ArtifactRenderer: React.FC<ArtifactRendererProps> = ({
  content,
  contentType,
  toolResults,
}) => {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [iframeContent, setIframeContent] = useState<string>('');

  const generateReactIframeContent = useCallback((reactCode: string) => {
    // Transform imports before injection
    const transformedCode = transformImports(reactCode);

    const iframeHTML = `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>React Artifact</title>
  
  <!-- External Libraries -->
  <script crossorigin src="https://unpkg.com/react@18/umd/react.development.js"></script>
  <script crossorigin src="https://unpkg.com/react-dom@18/umd/react-dom.development.js"></script>
  <script crossorigin src="https://unpkg.com/prop-types@15/prop-types.min.js"></script>
  <script src="https://unpkg.com/@babel/standalone/babel.min.js"></script>
  <script src="https://unpkg.com/recharts@2.8.0/umd/Recharts.js"></script>
  <script src="https://unpkg.com/lucide@latest/dist/umd/lucide.js"></script>
  <script src="https://unpkg.com/d3@7.8.5/dist/d3.min.js"></script>
  <script src="https://unpkg.com/lodash@4.17.21/lodash.min.js"></script>
  
  <!-- Tailwind CSS -->
  <script src="https://cdn.tailwindcss.com"></script>
  
  <style>
    body {
      margin: 0;
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif;
      background: white;
    }
    #root {
      width: 100%;
      height: 100%;
    }
    table {
      border-collapse: collapse;
      width: 100%;
    }
    th, td {
      border: 1px solid #ddd;
      padding: 8px;
      text-align: left;
    }
    th {
      background-color: #f2f2f2;
    }
    button {
      cursor: pointer;
      transition: background-color 0.2s;
    }
    button:hover {
      opacity: 0.8;
    }
  </style>
</head>
<body>
  <div id="root"></div>
  <script type="text/babel">
  
    
    
    // Expose Recharts components as globals so they work with or without import statements
    if (window.Recharts) {
      Object.keys(window.Recharts).forEach(function(key) {
        window[key] = window.Recharts[key];
      });
    }

    // Create React wrapper components for Lucide icons
    if (window.lucide) {
      window.LucideReactComponents = {};
      Object.keys(window.lucide).forEach(iconName => {
        const iconData = window.lucide[iconName];
        if (Array.isArray(iconData)) {
          // Create a React component wrapper for SVG path data
          window.LucideReactComponents[iconName] = (props = {}) => {
            const { className = 'w-6 h-6', ...otherProps } = props;
            return React.createElement('svg', {
              fill: 'none',
              stroke: 'currentColor',
              viewBox: '0 0 24 24',
              className,
              ...otherProps
            }, ...iconData.map((pathData, index) => 
              React.createElement('path', {
                key: index,
                d: Array.isArray(pathData) ? pathData.join(' ') : pathData,
                strokeLinecap: 'round',
                strokeLinejoin: 'round',
                strokeWidth: 2
              })
            ));
          };
        }
      });
    }
    
    // ErrorBoundary — catches render errors so the artifact degrades gracefully
    // instead of showing a blank screen. Also exposed as a global so LLM-generated
    // code can wrap individual sections for partial rendering.
    class ErrorBoundary extends React.Component {
      constructor(props) {
        super(props);
        this.state = { error: null };
      }
      static getDerivedStateFromError(error) {
        return { error };
      }
      render() {
        if (this.state.error) {
          return React.createElement('div', {
            style: {
              padding: '12px 16px',
              margin: '8px 0',
              background: '#fef2f2',
              border: '1px solid #fecaca',
              borderRadius: '8px',
              color: '#991b1b',
              fontSize: '13px',
              fontFamily: 'system-ui, sans-serif',
            }
          },
            React.createElement('strong', null, 'Render error: '),
            this.state.error.message
          );
        }
        return this.props.children;
      }
    }

    // User's React component code (with transformed imports)
    ${transformedCode}
    
    // Prepare toolResults data for injection
    const toolResults = ${JSON.stringify(toolResults || {})};
    
    // Only render App if it exists (not all artifacts will be React)
    if (typeof App !== 'undefined') {
      const root = ReactDOM.createRoot(document.getElementById('root'));
      root.render(
        <ErrorBoundary>
          <App toolResults={toolResults} />
        </ErrorBoundary>
      );
    }
  </script>
</body>
</html>`;

    setIframeContent(iframeHTML);
  }, [toolResults]);

  const generateHtmlIframeContent = useCallback((htmlCode: string) => {
    const trimmedCode = htmlCode.trim();
    const isCompleteDocument =
      trimmedCode.startsWith('<!DOCTYPE html>') || trimmedCode.startsWith('<html');

    if (isCompleteDocument) {
      // For complete HTML documents, use them directly (these are controlled artifacts, not user input)
      setIframeContent(htmlCode);
    } else {
      // For HTML snippets, sanitize and wrap in a basic HTML structure
      const sanitizedHTML = DOMPurify.sanitize(htmlCode, {
        ALLOWED_TAGS: [
          'div',
          'span',
          'p',
          'h1',
          'h2',
          'h3',
          'h4',
          'h5',
          'h6',
          'ul',
          'ol',
          'li',
          'table',
          'tr',
          'td',
          'th',
          'thead',
          'tbody',
          'canvas',
          'svg',
          'path',
          'circle',
          'rect',
          'line',
          'g',
          'button',
          'input',
          'select',
          'option',
          'textarea',
          'form',
          'a',
          'img',
          'br',
          'hr',
          'strong',
          'em',
          'b',
          'i',
          'u',
          'style',
          'script',
        ],
        ALLOWED_ATTR: [
          'class',
          'id',
          'style',
          'width',
          'height',
          'viewBox',
          'd',
          'fill',
          'stroke',
          'href',
          'src',
          'alt',
          'title',
          'data-*',
          'aria-*',
          'role',
          'onclick',
          'onchange',
          'onsubmit',
          'type',
          'value',
          'name',
          'placeholder',
          'disabled',
          'readonly',
          'checked',
          'selected',
        ],
        ALLOW_DATA_ATTR: true,
        ALLOW_ARIA_ATTR: true,
      });

      const iframeHTML = `
<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>HTML Artifact</title>
  <style>
    body {
      margin: 0;
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif;
    }
  </style>
</head>
<body>
  ${sanitizedHTML}
</body>
</html>`;

      setIframeContent(iframeHTML);
    }
  }, []);

  const generateJsIframeContent = useCallback((jsCode: string) => {
    // Sanitize JavaScript (basic approach)
    const sanitizedJS = DOMPurify.sanitize(jsCode);

    const iframeHTML = `
<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>JavaScript Artifact</title>
  <style>
    body {
      margin: 0;
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif;
    }
  </style>
</head>
<body>
  <div id="app"></div>
  <script>
    try {
      ${sanitizedJS}
    } catch (error) {
      document.getElementById('app').innerHTML = 
        '<div style="color: red;">Error: ' + error.message + '</div>';
    }
  </script>
</body>
</html>`;

    setIframeContent(iframeHTML);
  }, []);

  useEffect(() => {
    if (contentType === 'react') {
      generateReactIframeContent(content);
    } else if (contentType === 'html') {
      generateHtmlIframeContent(content);
    } else if (contentType === 'javascript') {
      generateJsIframeContent(content);
    }
  }, [content, contentType, generateReactIframeContent, generateHtmlIframeContent, generateJsIframeContent]);

  useEffect(() => {
    if (iframeRef.current && iframeContent) {
      iframeRef.current.srcdoc = iframeContent;
    }
  }, [iframeContent]);

  return (
    <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
      <iframe
        ref={iframeRef}
        title="Artifact Renderer"
        sandbox="allow-scripts"
        style={{
          width: '100%',
          height: '100%',
          border: 'none',
          flex: 1,
        }}
      />
    </Box>
  );
};
